<template>
    <div v-if="isLoading || isLoadingThreads || hasThreads" class="threads-container-wrapper d-flex flex-column h-100">
        <header class="conversation-header py-2">
            <div
                class="d-flex justify-content-between w-100 header-top px-3 pb-1"
                v-if="selectedConversation?.conversationGroupType"
            >
                <span class="group-name-text"
                    >{{ GroupType.getKeyUsingValue(selectedConversation.conversationGroupType) }} Group</span
                >
                <button
                    class="btn btn-primary compose-btn"
                    type="button"
                    v-if="!isLoading && !isLoadingThreads && canEditPatient"
                    @click="handleCompose"
                >
                    Compose
                </button>
            </div>
            <div class="px-3">
                <ul v-if="isLoading || isLoadingThreads" class="d-flex mt-1">
                    <span class="shimmer-loading w-10 me-4 lighten mt-1"></span>
                    <span class="shimmer-loading w-10 lighten mt-1"></span>
                </ul>
                <ul v-else-if="conversationThreadsInfo" class="d-flex mt-1">
                    <li
                        @click="handleActiveTab(FPTabsEnum.Thread)"
                        class="cursor-pointer me-4 fw-bold"
                        :class="{ 'border-bottom border-primary': activeTab == FPTabsEnum.Thread }"
                        style="border-width: 3px !important"
                    >
                        <button
                            class="link-btn nav-link menu-link"
                            :class="`${activeTab == FPTabsEnum.Thread ? 'text-primary' : 'text-secondary'}`"
                        >
                            <span>{{ formatDisplayUsers([conversationThreadsInfo?.patientDetail]) }}</span>
                        </button>
                    </li>
                    <li
                        @click="handleActiveTab(FPTabsEnum.FamilyGroup)"
                        class="cursor-pointer fw-bold"
                        :class="{ 'border-bottom border-primary': activeTab == FPTabsEnum.FamilyGroup }"
                        style="border-width: 3px !important"
                    >
                        <button
                            class="link-btn nav-link menu-link"
                            :class="`${activeTab == FPTabsEnum.FamilyGroup ? 'text-primary' : 'text-secondary'}`"
                        >
                            <span>Family Group</span>
                        </button>
                    </li>
                </ul>
            </div>
        </header>
        <div class="fp-conversation-body ps-4 w-100" v-show="activeTab == FPTabsEnum.Thread">
            <div
                class="h-100 pe-4 fp-conversation-main"
                :class="{ 'custom-scrollbar': userOs == 'Windows' }"
                ref="fpConversationMain"
            >
                <template v-if="isLoading || isLoadingThreads">
                    <div v-for="i in 2" :key="i" class="thread-card shimmer-thread">
                        <!-- Header -->
                        <div class="message-header">
                            <div class="sender-info">
                                <div class="ava-initials">
                                    <span></span>
                                </div>

                                <div class="message-text">
                                    <span class="shimmer-loading w-40 lighten"></span>
                                    <span class="shimmer-loading w-25 lighten mt-1"></span>
                                </div>
                            </div>

                            <span class="shimmer-loading w-15 lighten"></span>
                        </div>

                        <!-- Subject -->
                        <div class="message-subject mt-3">
                            <span class="shimmer-loading w-70 lighten"></span>
                        </div>

                        <!-- Body -->
                        <div class="message-body mt-2">
                            <span class="shimmer-loading w-90 lighten"></span>
                            <span class="shimmer-loading w-80 lighten mt-1"></span>
                        </div>

                        <!-- Reply placeholder spacing -->
                        <div class="mt-3">
                            <span class="shimmer-loading w-30 lighten"></span>
                        </div>
                    </div>
                </template>
                <template v-else>
                    <FPThreadsCard
                        v-for="thread in conversationThreadsInfo?.threads"
                        :key="thread.id"
                        :thread="thread"
                        :patientAgencyName="conversationThreadsInfo?.patientDetail?.agencyName"
                        :canEditPatient="canEditPatient"
                    />
                </template>
            </div>
        </div>
        <div class="w-100" v-show="activeTab == FPTabsEnum.FamilyGroup">
            <ul
                class="nav list-group list-group-flush message-navigation"
                :class="{ 'custom-scrollbar': userOs == 'Windows' }"
            >
                <template v-if="familyContacts && familyContacts.length">
                    <li
                        v-for="(contact, index) in familyContacts"
                        :key="contact?.id"
                        class="nav-item list-group-item message-list-item call-list call-list-height"
                    >
                        <div class="d-flex justify-content-between align-items-center min-width-0 h-100">
                            <div class="d-flex align-items-center">
                                <span class="expand-arrow" @click.stop="toggleDropdown(contact.calls, index)">
                                    <i :class="expandedIndex === index ? 'fas fa-angle-up' : 'fas fa-angle-down'"></i>
                                </span>
                                <div
                                    class="ava-initials flex-shrink-0 me-1"
                                    :class="`ava-initials-${getSolutionClassIndex(index)}`"
                                >
                                    <span>{{
                                        formatUserInitials({
                                            firstName: contact?.firstName,
                                            lastName: contact?.lastName
                                        })
                                    }}</span>
                                </div>
                                <div class="ps-2 flex-grow-1 min-width-0">
                                    <div class="d-flex align-items-center justify-content-between">
                                        <span class="mb-1 fw-bold text-dark">
                                            <span> {{ contact?.firstName }} {{ contact?.lastName }} </span>
                                        </span>
                                    </div>
                                    <span>{{ contact?.relationship }}</span>
                                </div>
                            </div>
                            <div class="d-flex align-items-center">
                                <span
                                    class="ms-1"
                                    @click="!dialogOpened && startMeeting(ConversationRoomTypeEnum.AudioCall, contact)"
                                    :class="[dialogOpened ? 'cursor-not-allowed' : 'cursor-pointer']"
                                    title="Audio Call"
                                    v-if="!isCallOngoing"
                                >
                                    <img src="../../../../../assets/images/chat/audio-call.svg" />
                                </span>
                                <span
                                    class="mx-2 mx-lg-4"
                                    @click="!dialogOpened && startMeeting(ConversationRoomTypeEnum.VideoCall, contact)"
                                    :class="[dialogOpened ? 'cursor-not-allowed' : 'cursor-pointer']"
                                    title="Video Call"
                                    v-if="!isCallOngoing"
                                >
                                    <img src="../../../../../assets/images/chat/video-call.svg" />
                                </span>
                            </div>
                        </div>
                        <div v-if="expandedIndex === index" class="call-history-panel table-container">
                            <div class="call-history-table">
                                <table class="famcall-history-table">
                                    <thead>
                                        <tr class="table-header">
                                            <th>Date and Time</th>
                                            <th>Call Duration</th>
                                            <th>Status</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr v-if="!selectedCallHistory.length">
                                            <td colspan="3" class="no-data">
                                                <h3>No Call History Found.</h3>
                                            </td>
                                        </tr>
                                        <tr v-for="call in selectedCallHistory" :key="call.id">
                                            <td>
                                                {{ formatDateTimeToLocal(call.startTime) }}
                                            </td>
                                            <td>
                                                {{ call.callDuration || "-" }}
                                            </td>
                                            <td>
                                                <span class="status-outgoing">
                                                    <img
                                                        class="sort-type-icon"
                                                        src="../../../../../assets/images/chat/outgoing-call.svg"
                                                    />
                                                    Outgoing
                                                </span>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </li>
                </template>
            </ul>
        </div>
        <template v-if="openCompose">
            <div class="d-flex w-100 align-items-baseline subject-input-div">
                <div class="form-group w-100">
                    <input
                        type="text"
                        class="form-control border-0"
                        placeholder="Enter Thread Subject (Required)"
                        maxlength="250"
                        v-alphanumeric-special-only
                        v-model="subject"
                        v-focus="focusOnInput"
                    />
                </div>
            </div>
            <MessageInputHandler
                :send="sendMessage"
                :should-disable-send="subject?.length < 1"
                :refs-name="`threadEditorRef-${selectedConversationId}`"
                :toggle-important="toggleImportant"
                :isFamilyPortal="true"
            />
        </template>
    </div>
</template>
<style src="./FPThreadsContainer.scss" />
<script lang="ts" src="./FPThreadsContainer.ts" />
